import { useMemo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

import { IconStarRateFill, Skeleton, Text, createMqStyles, tokens } from '@fhs/ui';

import { Tier, TieredProgressBar, tieredProgressBarConstants } from '../tiered-progress-bar';

interface Props {
  label?: string;
  points?: number;
  loading?: boolean;
  tiers: Tier[];
  style?: StyleProp<ViewStyle>;
  enableAnimation?: boolean;
}
function groupTiersByThreshold(tiers: Tier[], threshold: number = 10000): Tier[][] {
  const sortedTiers = [...tiers].sort((a, b) => a.value - b.value);
  const groups: Tier[][] = [];

  let currentGroup = [sortedTiers[0]];

  for (let i = 1; i < sortedTiers.length; i++) {
    const currentTier = sortedTiers[i];
    const lastTierInGroup = currentGroup[currentGroup.length - 1];
    const difference = currentTier.value - lastTierInGroup.value;

    if (difference <= threshold) {
      currentGroup.push(currentTier);
    } else {
      let addedToAGroup = false;
      if (currentGroup.length === 1) {
        currentGroup.push(currentTier);
        addedToAGroup = true;
      }
      groups.push([...currentGroup]);

      !addedToAGroup && groups.push([lastTierInGroup, currentTier]);
      currentGroup = [currentTier];
    }
  }

  if (currentGroup.length > 1) {
    groups.push([...currentGroup]);
  }

  return groups;
}

function findGroupForValue(tierGroups: Tier[][], value: number): Tier[] {
  for (const group of tierGroups) {
    const minValue = Math.min(...group.map(tier => tier.value));
    const maxValue = Math.max(...group.map(tier => tier.value));

    if (value >= minValue && value <= maxValue) {
      return group;
    }
  }

  return [];
}

function Dots() {
  const styles = useMqStyles();

  return (
    <View style={styles.dotsContainer}>
      <View style={styles.dot} />
      <View style={styles.dot} />
      <View style={styles.dot} />
      <View style={styles.dot} />
    </View>
  );
}
export function PointsProgress({
  label = "You've got",
  tiers,
  points,
  style,
  loading,
  enableAnimation = true,
}: Props) {
  const styles = useMqStyles();
  const tierGroups = useMemo(() => groupTiersByThreshold(tiers, 10000), [tiers]);
  const currentGroup = useMemo(
    () => (typeof points === 'number' ? findGroupForValue(tierGroups, points) : []),
    [tierGroups, points]
  );
  const biggestTierValue = useMemo(() => Math.max(...tiers.map(tier => tier.value)), [tiers]);
  const hideFlameIcon = points === 0;
  const hideProgressBar = typeof points !== 'number' || loading || points > biggestTierValue;
  const renderDotsInTheEnd = currentGroup === tierGroups[0];

  return (
    <View style={[styles.container, style]}>
      <Text.Ui size="md">{label}</Text.Ui>
      <View style={styles.pointsContainer}>
        <IconStarRateFill color={tokens.colors.$houseYellow} size={28} />
        {loading ? (
          <Skeleton style={styles.pointsSkeleton} />
        ) : (
          <Text.Heading type="two" style={styles.pointsText}>
            {points?.toLocaleString()} points
          </Text.Heading>
        )}
      </View>
      {!hideProgressBar && (
        <View style={styles.progressContainer}>
          {!renderDotsInTheEnd && <Dots />}
          <TieredProgressBar
            style={styles.progressBar}
            hideFlameIcon={hideFlameIcon}
            currentValue={points}
            tiers={currentGroup}
            enableAnimation={enableAnimation}
          />
          {renderDotsInTheEnd && <Dots />}
        </View>
      )}
    </View>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
  pointsContainer: {
    $base: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingVertical: 5,
    },
  },
  pointsSkeleton: {
    $base: {
      marginRight: 4,
      width: 90,
      height: 16,
      borderRadius: 4,
    },
  },
  pointsText: {
    $base: {
      paddingTop: 1,
    },
    $gteDesktop: {
      fontSize: 30,
    },
  },
  progressContainer: {
    $base: {
      width: '100%',
      flexDirection: 'row',
    },
  },
  progressBar: {
    $base: {
      flex: 1,
    },
  },
  dotsContainer: {
    $base: {
      flexDirection: 'row',
      gap: 4,
      marginTop:
        tieredProgressBarConstants.fireHeight -
        tieredProgressBarConstants.barHeight +
        tieredProgressBarConstants.tierDotSize / 2,
    },
  },
  dot: {
    $base: {
      width: tieredProgressBarConstants.tierDotSize,
      height: tieredProgressBarConstants.tierDotSize,
      borderRadius: 3,
      backgroundColor: tokens.colors.$blackOpacity10,
    },
  },
});
